import { LitElement, html, css } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { Firestore } from 'firebase/firestore';
import { 
  getWorkOrdersByCompany,
  getAssetsByCompany,
  getPartsByCompany,
  getPreventiveMaintenanceByCompany,
  WorkOrder
} from '@cmms/api';
import { WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';

@customElement('engineer-dashboard')
export class EngineerDashboard extends LitElement {
  static styles = css`
    :host {
      display: block;
      padding: 1.5rem;
    }

    .welcome-section {
      background: linear-gradient(135deg, var(--sl-color-primary-600) 0%, var(--sl-color-primary-800) 100%);
      color: white;
      padding: 2rem;
      border-radius: var(--sl-border-radius-large);
      margin-bottom: 2rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .welcome-section h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.75rem;
      font-weight: 600;
    }

    .welcome-section p {
      margin: 0;
      opacity: 0.9;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: white;
      border-radius: var(--sl-border-radius-medium);
      padding: 1.5rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border-left: 4px solid var(--sl-color-primary-500);
    }

    .stat-card .label {
      font-size: 0.875rem;
      color: var(--sl-color-neutral-600);
      margin-bottom: 0.5rem;
    }

    .stat-card .value {
      font-size: 2rem;
      font-weight: 600;
      color: var(--sl-color-neutral-900);
    }

    .stat-card.warning { border-left-color: var(--sl-color-warning-500); }
    .stat-card.success { border-left-color: var(--sl-color-success-500); }
    .stat-card.danger { border-left-color: var(--sl-color-danger-500); }

    .content-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .content-section {
      background: white;
      border-radius: var(--sl-border-radius-medium);
      padding: 1.5rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }

    .content-section h2 {
      margin: 0 0 1rem 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--sl-color-neutral-900);
    }

    .report-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .report-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem;
      background: var(--sl-color-neutral-50);
      border-radius: var(--sl-border-radius-medium);
      border-left: 3px solid var(--sl-color-primary-500);
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .report-item:hover {
      background: var(--sl-color-neutral-100);
      transform: translateX(4px);
    }

    .report-item .info {
      flex: 1;
    }

    .report-item .title {
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: var(--sl-color-neutral-900);
    }

    .report-item .time {
      font-size: 0.875rem;
      color: var(--sl-color-neutral-600);
    }

    .badge {
      padding: 0.25rem 0.75rem;
      border-radius: var(--sl-border-radius-pill);
      font-size: 0.75rem;
      font-weight: 600;
    }

    .badge.open {
      background: var(--sl-color-warning-100);
      color: var(--sl-color-warning-700);
    }

    .badge.in-progress {
      background: var(--sl-color-primary-100);
      color: var(--sl-color-primary-700);
    }

    .badge.completed {
      background: var(--sl-color-success-100);
      color: var(--sl-color-success-700);
    }

    .badge.high {
      background: var(--sl-color-danger-100);
      color: var(--sl-color-danger-700);
    }

    .badge.medium {
      background: var(--sl-color-warning-100);
      color: var(--sl-color-warning-700);
    }

    .badge.low {
      background: var(--sl-color-neutral-100);
      color: var(--sl-color-neutral-700);
    }

    sl-spinner {
      display: block;
      margin: 2rem auto;
    }

    .empty-state {
      text-align: center;
      padding: 2rem;
      color: var(--sl-color-neutral-600);
    }

    .empty-state sl-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      opacity: 0.3;
    }
  `;

  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @property({ type: Object }) user: any = null;

  @state() private isLoading = true;
  @state() private assignedWorkOrders: WorkOrder[] = [];
  @state() private recentWorkOrders: WorkOrder[] = [];
  @state() private duePMs: any[] = [];
  @state() private stats = {
    assignedWOs: 0,
    openWOs: 0,
    duePMs: 0,
    totalAssets: 0
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.companyId) {
      this.loadDashboardData();
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId') && this.companyId) {
      this.loadDashboardData();
    }
  }

  private async loadDashboardData() {
    if (!this.firestore || !this.companyId) return;

    this.isLoading = true;
    try {
      await Promise.all([
        this.loadWorkOrders(),
        this.loadAssets(),
        this.loadPMs()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadWorkOrders() {
    try {
      const result = await getWorkOrdersByCompany(this.firestore, this.companyId);
      
      if (result.data && result.data.length > 0) {
        const allWOs = result.data;
        
        // Get assigned work orders
        const assignedWOs = allWOs
          .filter((wo: any) => wo.model?.assigned_to === this.user?.uid)
          .sort((a: any, b: any) => {
            const dateA = a.model?.created_at || 0;
            const dateB = b.model?.created_at || 0;
            return dateB - dateA;
          });
        
        this.assignedWorkOrders = assignedWOs.slice(0, 5) as WorkOrder[];
        this.stats.assignedWOs = assignedWOs.length;
        
        // Get unassigned work orders (available for engineers to claim)
        const unassignedWOs = allWOs
          .filter((wo: any) => 
            (wo.model?.assigned_to === '' || !wo.model?.assigned_to) &&
            wo.model?.status_cat !== WorkOrderStatusCat.COMPLETED
          )
          .sort((a: any, b: any) => {
            // Sort by priority first (high priority first), then by date
            const priorityA = a.model?.priority_cat || 2;
            const priorityB = b.model?.priority_cat || 2;
            if (priorityA !== priorityB) {
              return priorityB - priorityA; // High to low
            }
            const dateA = a.model?.created_at || 0;
            const dateB = b.model?.created_at || 0;
            return dateB - dateA;
          });
        
        this.recentWorkOrders = unassignedWOs.slice(0, 5) as WorkOrder[];
        this.stats.openWOs = unassignedWOs.length;
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
    }
  }

  private async loadAssets() {
    try {
      const result = await getAssetsByCompany(this.firestore, this.companyId);
      if (result.data) {
        this.stats.totalAssets = result.data.length;
      }
    } catch (error) {
      console.error('Error loading assets:', error);
    }
  }

  private async loadPMs() {
    try {
      const result = await getPreventiveMaintenanceByCompany(this.firestore, this.companyId);
      if (result.data) {
        const now = Date.now();
        const duePMs = result.data.filter((pm: any) => 
          pm.model?.pm_due_at && pm.model.pm_due_at <= now + 7 * 24 * 60 * 60 * 1000 // Due within 7 days
        );
        this.duePMs = duePMs.slice(0, 5);
        this.stats.duePMs = duePMs.length;
      }
    } catch (error) {
      console.error('Error loading PMs:', error);
    }
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hrs ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  }

  private getWorkOrderStatusBadge(statusCat: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN:
        return 'open';
      case WorkOrderStatusCat.IN_PROGRESS:
        return 'in-progress';
      case WorkOrderStatusCat.COMPLETED:
        return 'completed';
      default:
        return 'open';
    }
  }

  private getWorkOrderStatusLabel(statusCat: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN:
        return 'Open';
      case WorkOrderStatusCat.IN_PROGRESS:
        return 'In Progress';
      case WorkOrderStatusCat.COMPLETED:
        return 'Completed';
      default:
        return 'Open';
    }
  }

  private getPriorityBadge(priorityCat: number) {
    switch (priorityCat) {
      case WorkOrderPriorityCat.HIGH:
        return 'high';
      case WorkOrderPriorityCat.MEDIUM:
        return 'medium';
      case WorkOrderPriorityCat.LOW:
        return 'low';
      default:
        return 'medium';
    }
  }

  private getPriorityLabel(priorityCat: number) {
    switch (priorityCat) {
      case WorkOrderPriorityCat.HIGH:
        return 'High';
      case WorkOrderPriorityCat.MEDIUM:
        return 'Medium';
      case WorkOrderPriorityCat.LOW:
        return 'Low';
      default:
        return 'Medium';
    }
  }

  render() {
    if (this.isLoading) {
      return html`<sl-spinner size="large"></sl-spinner>`;
    }

    const userName = this.user?.displayName || this.user?.name || 'Engineer';

    return html`
      <div class="welcome-section">
        <h1>Welcome back, ${userName}! 👋</h1>
        <p>Monitor your assigned work and upcoming tasks</p>
      </div>

      <!-- Stats -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="label">Assigned to Me</div>
          <div class="value">${this.stats.assignedWOs}</div>
        </div>
        <div class="stat-card warning">
          <div class="label">Open Work Orders</div>
          <div class="value">${this.stats.openWOs}</div>
        </div>
        <div class="stat-card danger">
          <div class="label">Due PMs</div>
          <div class="value">${this.stats.duePMs}</div>
        </div>
        <div class="stat-card success">
          <div class="label">Total Assets</div>
          <div class="value">${this.stats.totalAssets}</div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Assigned Work Orders -->
        <div class="content-section">
          <h2>📋 My Assigned Work Orders</h2>
          ${this.assignedWorkOrders.length > 0 ? html`
            <div class="report-list">
              ${this.assignedWorkOrders.map((wo: any) => html`
                <div class="report-item">
                  <div class="info">
                    <div class="title">${wo.model?.description || 'No description'}</div>
                    <div class="time">${this.formatDate(wo.model?.created_at || Date.now())}</div>
                  </div>
                  <div style="display: flex; gap: 0.5rem;">
                    <span class="badge ${this.getPriorityBadge(wo.model?.priority_cat)}">
                      ${this.getPriorityLabel(wo.model?.priority_cat)}
                    </span>
                    <span class="badge ${this.getWorkOrderStatusBadge(wo.model?.status_cat)}">
                      ${this.getWorkOrderStatusLabel(wo.model?.status_cat)}
                    </span>
                  </div>
                </div>
              `)}
            </div>
          ` : html`
            <div class="empty-state">
              <sl-icon name="check-circle"></sl-icon>
              <p>No assigned work orders</p>
            </div>
          `}
        </div>

        <!-- Recent Work Orders -->
        <div class="content-section">
          <h2>📋 Available Work Orders</h2>
          ${this.recentWorkOrders.length > 0 ? html`
            <div class="report-list">
              ${this.recentWorkOrders.map((wo: any) => html`
                <div class="report-item">
                  <div class="info">
                    <div class="title">${wo.model?.description || 'No description'}</div>
                    <div class="time">${this.formatDate(wo.model?.created_at || Date.now())}</div>
                  </div>
                  <div style="display: flex; gap: 0.5rem;">
                    <span class="badge ${this.getPriorityBadge(wo.model?.priority_cat)}">
                      ${this.getPriorityLabel(wo.model?.priority_cat)}
                    </span>
                    <span class="badge ${this.getWorkOrderStatusBadge(wo.model?.status_cat)}">
                      ${this.getWorkOrderStatusLabel(wo.model?.status_cat)}
                    </span>
                  </div>
                </div>
              `)}
            </div>
          ` : html`
            <div class="empty-state">
              <sl-icon name="clipboard-x"></sl-icon>
              <p>No available work orders</p>
            </div>
          `}
        </div>
      </div>

      <!-- Due PMs -->
      ${this.duePMs.length > 0 ? html`
        <div class="content-section">
          <h2>⏰ Upcoming Preventive Maintenance</h2>
          <div class="report-list">
            ${this.duePMs.map((pm: any) => html`
              <div class="report-item">
                <div class="info">
                  <div class="title">${pm.model?.description || 'PM Task'}</div>
                  <div class="time">Due: ${pm.model?.pm_due_at ? new Date(pm.model.pm_due_at).toLocaleDateString() : 'N/A'}</div>
                </div>
                <span class="badge warning">Due Soon</span>
              </div>
            `)}
          </div>
        </div>
      ` : ''}
    `;
  }
}
